import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid2,
  Paper,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import {
  Configuration,
  ExtendedFieldError,
  FieldDefinition,
} from "product-test-types";
import { CHARACTER_LIMITATIONS } from "product-test-utils";
import { VIEW_NAMES } from "product-test-utils";
import React from "react";

import { getItemName } from "../../utils/get-plural-of-item-name";
import { AntSwitch } from "../components/ant-switch";
import { CategoryFields } from "../custom-fields/category-field";
import { DefaultFields } from "../custom-fields/default-field";
import { usePimoForm } from "../pimo-form";

export interface ItemBasicFieldsFormProps {
  configuration?: Configuration;
  language?: string;
  setUpdatedFieldsFlag: (change: "FieldActivated" | "FieldDeactivated") => void;
}

const fieldTypeTranslationKeyByType = {
  Date: "datatype.date",
  Dropdown: "datatype.select",
  Number: "datatype.number",
  Select: "datatype.select",
  Text: "datatype.text",
} as const;

const fieldLabelTranslationKeyByFieldName: Record<
  string,
  Record<string, string>
> = {
  category: {
    Category: "category",
  },
  customId: {
    "Custom ID": "customId",
    ID: "customId",
  },
  description: {
    Description: "description",
  },
  dropdownFieldOne: {
    Category: "category",
    Team: "team",
  },
  endDate: {
    "End Date": "endDate",
  },
  freeTextFieldOne: {
    "Audit Title": "freeTextFieldOne",
  },
  name: {
    Name: "name",
  },
  maturity: {
    Maturity: "maturity",
    maturity: "maturity",
  },
  owner: {
    Owner: "owner",
  },
  secondCategory: {
    "Audit Type": "auditType",
    Category: "category",
  },
  startDate: {
    "Start Date": "startDate",
  },
};

const localizeDataFieldLabel = (
  language: string,
  field: Pick<FieldDefinition, "fieldName" | "label">
) => {
  const translationKey =
    fieldLabelTranslationKeyByFieldName[field.fieldName]?.[field.label];

  return translationKey
    ? getLocalizedMessage(language, translationKey) || field.label
    : field.label;
};

const localizeDataFieldType = (language: string, type: string) =>
  getLocalizedMessage(
    language,
    fieldTypeTranslationKeyByType[
      type as keyof typeof fieldTypeTranslationKeyByType
    ]
  ) || type;

export const ItemBasicFieldsForm: PimoReactComponent<
  ItemBasicFieldsFormProps,
  "define:item-data",
  Configuration
> = ({ language = "en", setUpdatedFieldsFlag }) => {
  const { setValue, formState } = usePimoForm<{
    configuration: Configuration;
    validationErrors: Record<string, ExtendedFieldError>;
    enableSaveButton: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });

  const itemName = getItemName(formState.configuration?.itemName, language);
  const [isAccordionExpanded, setIsAccordionExpanded] = React.useState<
    string | false
  >(false);

  const orderedItemBasicFields = (
    formState.configuration?.fieldsDefinition ?? []
  )
    .filter((f) => f.purpose === "itemBasic")
    .sort((a, b) => {
      const preferredOrder = ["name", "customId", "maturity"];
      const indexA = preferredOrder.indexOf(a.fieldName);
      const indexB = preferredOrder.indexOf(b.fieldName);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      if (indexA !== -1) {
        return -1;
      }
      if (indexB !== -1) {
        return 1;
      }

      return 0;
    });

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, newExpanded: boolean) => {
      setIsAccordionExpanded(newExpanded ? panel : false);
    };
  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "calc(100vh - 83px)",
        overflowY: "auto",
      }}
    >
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 500,
        }}
      >
        {getLocalizedMessage(language, "dataModel.masterDataItem", {
          itemName,
        }) || `Master Data ${itemName}`}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          color: grey[600],
        }}
      >
        {itemName}{" "}
        {getLocalizedMessage(language, "dataModel.itemMasterDataDescription") ||
          `master data is the data that is entered when a new ${itemName} is created. Please select the data fields`}
      </Typography>

      <Paper
        sx={{
          borderRadius: "12px",
          boxShadow: "none",
          border: "1px solid",
          borderColor: grey[300],
        }}
      >
        <Grid2
          size={12}
          container
          alignItems="center"
          sx={{ height: 65, p: 2 }}
        >
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataField") || "Data Field"}
          </Grid2>
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataType") || "Data Type"}
          </Grid2>
          <Grid2 size={3}></Grid2>
          <Grid2 size={3}></Grid2>
        </Grid2>

        {orderedItemBasicFields.map((field) => (
          <Accordion
            expanded={isAccordionExpanded === field.fieldName}
            onChange={handleChange(field.fieldName)}
            elevation={0}
            key={field.fieldName + field.purpose}
            sx={{
              "&:last-of-type": {
                borderRadius: "12px",
              },
            }}
          >
            <AccordionSummary
              aria-controls="panel1-content"
              id="panel1-header"
              expandIcon={field.type === "Dropdown" ? <ExpandMoreIcon /> : null}
              sx={{
                "& .MuiAccordionSummary-content": {
                  marginLeft: 0,
                },
                cursor: "default",
                userSelect: "text",
                width: field.type === "Dropdown" ? "100%" : "98%",
                height: 65,
              }}
            >
              <Grid2
                size={12}
                container
                alignItems="center"
                key={field.fieldName + field.purpose + "grid"}
                sx={{ height: 65 }}
              >
                <Grid2 size={3} sx={{ fontSize: "14px" }}>
                  {localizeDataFieldLabel(language, field)}
                </Grid2>
                <Grid2 size={3} sx={{ fontSize: "14px" }}>
                  {localizeDataFieldType(language, field.type)}
                </Grid2>
                <Grid2 size={3}>
                  <FormControlLabel
                    labelPlacement="start"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                      },
                    }}
                    control={
                      <Checkbox
                        onClick={(e) => {
                          // stop the click from bubbling to AccordionSummary
                          e.stopPropagation();
                        }}
                        onChange={(e) => {
                          const currentField = {
                            ...field,
                            isMandatory: e.target.checked,
                          };
                          const newFieldsDefinition =
                            formState.configuration?.fieldsDefinition ?? [];
                          // Find the correct index in the full array
                          const fullArrayIndex = newFieldsDefinition.findIndex(
                            (f) =>
                              f.fieldName === field.fieldName &&
                              f.purpose === field.purpose
                          );

                          if (fullArrayIndex !== -1) {
                            newFieldsDefinition[fullArrayIndex] = currentField;
                            setValue(
                              `configuration.fieldsDefinition`,
                              newFieldsDefinition
                            );
                          }
                          if (formState.enableSaveButton !== true) {
                            setValue("enableSaveButton", true);
                          }
                        }}
                        disabled={!field.isEditable}
                        icon={
                          <span
                            style={{
                              width: 20,
                              height: 20,
                              border: "2px solid",
                              borderColor: grey[700],
                              display: "inline-block",
                              backgroundColor: "#fff",
                            }}
                          />
                        }
                        checkedIcon={
                          <span
                            style={{
                              width: 20,
                              height: 20,

                              border: "2px solid",
                              borderColor: !field.isEditable
                                ? grey[400]
                                : grey[700],
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#fff",
                            }}
                          >
                            <CheckIcon
                              style={{
                                fontSize: 18,
                                color: !field.isEditable
                                  ? grey[400]
                                  : grey[700],
                              }}
                            />
                          </span>
                        }
                        checked={field.isMandatory}
                      />
                    }
                    label={
                      getLocalizedMessage(language, "mandatory") || "Mandatory"
                    }
                  />
                </Grid2>
                <Grid2 size={3} sx={{ fontSize: "14px" }}>
                  {field.fieldName === "customId" ||
                  field.fieldName === "name" ||
                  field.fieldName === "maturity" ? (
                    <Chip
                      label={
                        getLocalizedMessage(
                          language,
                          "constantlyActiveField"
                        ) || "Constantly Active Field"
                      }
                      sx={{
                        color: "#008336",
                        fontWeight: 500,
                        backgroundColor: "#e6f9e6",
                      }}
                    ></Chip>
                  ) : (
                    <FormControlLabel
                      control={
                        <AntSwitch
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          checked={field.enabled}
                          onChange={(e) => {
                            const currentField = {
                              ...field,
                              enabled: e.target.checked,
                            };

                            const newFieldsDefinition = [
                              ...(formState.configuration?.fieldsDefinition ??
                                []),
                            ];
                            // Find the correct index in the full array
                            const fullArrayIndex =
                              newFieldsDefinition.findIndex(
                                (f) =>
                                  f.fieldName === field.fieldName &&
                                  f.purpose === field.purpose
                              );

                            if (fullArrayIndex !== -1) {
                              newFieldsDefinition[fullArrayIndex] =
                                currentField;
                              setValue(
                                `configuration.fieldsDefinition`,
                                newFieldsDefinition
                              );
                            }
                            if (
                              !e.target.checked &&
                              field.fieldInConfiguration
                            ) {
                              const newErrors = {
                                ...(formState.validationErrors ?? {}),
                              };
                              Object.keys(newErrors).forEach((k) => {
                                if (
                                  k.startsWith(
                                    `${field.fieldInConfiguration}.`
                                  ) ||
                                  k === field.fieldInConfiguration
                                )
                                  delete newErrors[k];
                              });
                              setValue("validationErrors", newErrors);
                            }
                            setValue("enableSaveButton", true);
                            setUpdatedFieldsFlag(
                              e.target.checked
                                ? "FieldActivated"
                                : "FieldDeactivated"
                            );
                          }}
                        />
                      }
                      label={
                        getLocalizedMessage(language, "activate") || "Activate"
                      }
                      labelPlacement="start"
                      sx={{
                        "& .MuiSwitch-thumb": {
                          backgroundColor: grey[300],
                        },
                        "& .MuiSwitch-track": {
                          color: "#6fe255",
                        },
                        "& .MuiFormControlLabel-label": {
                          fontSize: "14px",
                        },
                      }}
                    />
                  )}
                </Grid2>
              </Grid2>
            </AccordionSummary>
            {renderTheCorrectComponent(
              setValue,
              formState.configuration,
              formState.validationErrors,
              field,
              formState.enableSaveButton
            )}
          </Accordion>
        ))}
      </Paper>
    </Box>
  );
};

const renderTheCorrectComponent = (
  setValue: (key: string, value: unknown) => void,
  configuration: Configuration | undefined,
  validationErrors: Record<string, ExtendedFieldError>,
  field: FieldDefinition & {
    purpose:
      "projectReporting" | "itemReporting" | "projectBasic" | "itemBasic";
  },
  enableSaveButton?: boolean
) => {
  switch (field.fieldInConfiguration) {
    case "dropdownFieldOneDefinition":
    case "maturityConfiguration":
    case "dimensionDefinition":
      const configKey:
        | "dropdownFieldOneDefinition"
        | "maturityConfiguration"
        | "dimensionDefinition" = field.fieldInConfiguration;

      return (
        <AccordionDetails>
          <CategoryFields
            baseFieldName={configKey}
            initialEntries={
              configKey === "maturityConfiguration"
                ? (configuration?.maturityConfiguration ?? [])
                : configKey === "dimensionDefinition"
                  ? (configuration?.dimensionDefinition ?? [])
                  : (configuration?.dropdownFieldOneDefinition ?? [])
            }
            onEntriesChange={(val) => {
              setValue(`configuration.${configKey}`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              Object.keys(newErrors).forEach((k) => {
                if (k.startsWith(`${configKey}.`)) delete newErrors[k];
              });

              const maxLength =
                CHARACTER_LIMITATIONS[
                  `${configKey}.{idx}.name` as keyof typeof CHARACTER_LIMITATIONS
                ] ?? 30;

              if (field.enabled) {
                val.forEach((item, index) => {
                  if ((item.name ?? "").trim() === "") {
                    newErrors[`${configKey}.${index}.name`] = {
                      type: "required",
                      message: "This field is required",
                      tabContainingErrors: VIEW_NAMES[3],
                    } as ExtendedFieldError;
                  } else if ((item.name ?? "").length > maxLength) {
                    newErrors[`${configKey}.${index}.name`] = {
                      type: "maxLength",
                      message: `Must be ${maxLength} characters or less`,
                      tabContainingErrors: VIEW_NAMES[3],
                    } as ExtendedFieldError;
                  }
                });
              }

              setValue("validationErrors", newErrors);
              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            fieldName={field.label}
            onFieldNameChange={(name) => {
              const indexOfField =
                configuration?.fieldsDefinition?.findIndex(
                  (f) =>
                    f.fieldName === field.fieldName &&
                    f.purpose === field.purpose
                ) ?? 0;
              const newFieldsDefinition = configuration?.fieldsDefinition ?? [];
              newFieldsDefinition[indexOfField] = {
                ...newFieldsDefinition[indexOfField],
                label: name,
              };

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };
              if (field.enabled) {
                if (name.trim() === "") {
                  const newErrors: Record<string, ExtendedFieldError> = {
                    ...(validationErrors ?? {}),
                  };
                  newErrors[String(configKey)] = {
                    type: "required",
                    message: "This field is required",
                    tabContainingErrors: `Master Data ${configuration?.itemName}`,
                  } as ExtendedFieldError;
                  setValue("validationErrors", newErrors);
                } else {
                  const newErrors = { ...(validationErrors ?? {}) };
                  delete newErrors[String(configKey)];
                  setValue("validationErrors", newErrors);
                }
              }

              setValue("validationErrors", newErrors);
              setValue(`configuration.fieldsDefinition`, newFieldsDefinition);
              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            onValidationChange={(fieldPath, value, maxLength) => {
              const newErrors = { ...(validationErrors ?? {}) };
              if (field.enabled && value.length > maxLength) {
                newErrors[fieldPath] = {
                  type: "maxLength",
                  message: `Must be ${maxLength} characters or less`,
                  tabContainingErrors: VIEW_NAMES[3],
                } as ExtendedFieldError;
              } else {
                delete newErrors[fieldPath];
              }
              setValue("validationErrors", newErrors);
            }}
            validationErrors={validationErrors}
            maxEntries={configKey === "dimensionDefinition" ? 10 : 5}
            withColor={
              configKey === "dimensionDefinition" ||
              configKey === "maturityConfiguration"
            }
          />
        </AccordionDetails>
      );
    case "secondCategoryDefinition":
      return (
        <AccordionDetails>
          <CategoryFields
            baseFieldName="secondCategoryDefinition"
            initialEntries={configuration?.secondCategoryDefinition ?? []}
            onEntriesChange={(val) => {
              setValue(`configuration.secondCategoryDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              Object.keys(newErrors).forEach((k) => {
                if (k.startsWith("secondCategoryDefinition."))
                  delete newErrors[k];
              });

              if (field.enabled) {
                val.forEach((item, index) => {
                  if ((item.name ?? "").trim() === "") {
                    newErrors[`secondCategoryDefinition.${index}.name`] = {
                      type: "required",
                      message: "Status name cannot be empty",
                      tabContainingErrors: VIEW_NAMES[3],
                    } as ExtendedFieldError;
                  } else if ((item.name ?? "").length > 30) {
                    newErrors[`secondCategoryDefinition.${index}.name`] = {
                      type: "maxLength",
                      message: "Status name must be 30 characters or less",
                      tabContainingErrors: VIEW_NAMES[3],
                    } as ExtendedFieldError;
                  }
                });
              }

              setValue("validationErrors", newErrors);
            }}
            fieldName={field.label}
            onFieldNameChange={(name) => {
              const index =
                configuration?.fieldsDefinition?.findIndex(
                  (f) =>
                    f.fieldName === field.fieldName &&
                    f.purpose === field.purpose
                ) ?? 0;
              const newFieldsDefinition = configuration?.fieldsDefinition ?? [];
              newFieldsDefinition[index] = {
                ...newFieldsDefinition[index],
                label: name,
              };
              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };
              if (name.trim() === "") {
                newErrors[`secondCategoryDefinition`] = {
                  type: "required",
                  message: "Field label cannot be empty",
                  tabContainingErrors: VIEW_NAMES[3],
                } as ExtendedFieldError;
              } else if (name.length > 30) {
                newErrors[`secondCategoryDefinition`] = {
                  type: "maxLength",
                  message: "Field label must be 30 characters or less",
                  tabContainingErrors: VIEW_NAMES[3],
                } as ExtendedFieldError;
              } else {
                delete newErrors[`secondCategoryDefinition`];
              }
              setValue("validationErrors", newErrors);
              setValue(`configuration.fieldsDefinition`, newFieldsDefinition);
            }}
            onValidationChange={(fieldPath, value, maxLength) => {
              const newErrors = { ...(validationErrors ?? {}) };
              if (field.enabled && value.length > maxLength) {
                newErrors[fieldPath] = {
                  type: "maxLength",
                  message: `Must be ${maxLength} characters or less`,
                  tabContainingErrors: VIEW_NAMES[3],
                } as ExtendedFieldError;
              } else {
                delete newErrors[fieldPath];
              }
              setValue("validationErrors", newErrors);
            }}
            validationErrors={validationErrors}
          />
        </AccordionDetails>
      );
    default:
      return (
        <AccordionDetails>
          <DefaultFields />
        </AccordionDetails>
      );
  }
};
